import React, { useState, useEffect } from 'react';
import { FaTimes, FaCheck, FaExternalLinkAlt, FaChevronDown, FaChevronUp, FaLanguage, FaGlobe } from 'react-icons/fa';
import { authenticatedFetch, getBaseUrl } from '../utils/tokenUtils';

const PREDEFINED_LANGS = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪' },
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'he', label: 'עברית', flag: '🇮🇱' },
  { code: 'fa', label: 'فارسی', flag: '🇮🇷' },
  { code: 'az', label: 'Azərbaycan', flag: '🇦🇿' },
  { code: 'kk', label: 'Қазақ тілі', flag: '🇰🇿' },
  { code: 'uz', label: 'Oʻzbekcha', flag: '🇺🇿' }
];

const EditPlacementModal = ({ placement, projects, onClose, onSuccess }) => {
  // Safe converter: Map | Array<[k,v]> | plain object -> plain object
  const mapToObject = (input) => {
    if (!input) return {};
    if (Array.isArray(input)) {
      return input.reduce((acc, pair) => {
        if (Array.isArray(pair) && pair.length >= 2) {
          acc[String(pair[0])] = pair[1];
        }
        return acc;
      }, {});
    }
    if (typeof input === 'object') {
      return input;
    }
    return {};
  };
  const [formData, setFormData] = useState({
    name: placement.name,
    selectedProjects: placement.selectedProjects?.map(p => p._id) || [],
    settings: placement.settings,
    status: placement.status || 'active',
    isActive: placement.isActive !== undefined ? placement.isActive : true,
    platformStatus: placement.platformStatus || { android: true, ios: true, web: true },
    projectOverrides: (placement.projectOverrides || []).map(po => ({
      projectId: po.projectId,
      title: mapToObject(po.title),
      description: mapToObject(po.description)
    }))
  });
  // Re-init form when placement changes (ensures saved overrides prefill UI)
  useEffect(() => {
    setFormData({
      name: placement.name,
      selectedProjects: placement.selectedProjects?.map(p => p._id) || [],
      settings: placement.settings,
      status: placement.status || 'active',
      isActive: placement.isActive !== undefined ? placement.isActive : true,
      platformStatus: placement.platformStatus || { android: true, ios: true, web: true },
      projectOverrides: (placement.projectOverrides || []).map(po => ({
        projectId: po.projectId,
        title: mapToObject(po.title),
        description: mapToObject(po.description)
      }))
    });
  }, [placement]);
  const [loading, setLoading] = useState(false);
  const [errorToast, setErrorToast] = useState({ show: false, message: '' });

  // Get all projects except the current placement's project
  const availableProjects = projects.filter(project => project._id !== placement.projectId?._id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation: for each selected project, for each active language ensure non-empty fields
      const errors = [];
      const selectedSet = new Set((formData.selectedProjects || []).map(id => String(id)));
      (formData.projectOverrides || []).forEach(po => {
        if (!selectedSet.has(String(po.projectId))) return;
        const langs = Array.from(new Set([...(Object.keys(po.title || {})), ...(Object.keys(po.description || {}))]));
        langs.forEach(code => {
          const t = (po.title || {})[code];
          const d = (po.description || {})[code];
          if ((t === undefined || String(t).trim() === '') || (d === undefined || String(d).trim() === '')) {
            const proj = (availableProjects.find(p => String(p._id) === String(po.projectId)) || projects.find(p => String(p._id) === String(po.projectId))) || {};
            const missing = [];
            if (t === undefined || String(t).trim() === '') missing.push('Title');
            if (d === undefined || String(d).trim() === '') missing.push('Description');
            errors.push(`${proj.name || 'Project'} — ${code.toUpperCase()}: ${missing.join(' & ')} required`);
          }
        });
      });
      if (errors.length > 0) {
        setErrorToast({ show: true, message: errors[0] });
        setTimeout(() => setErrorToast({ show: false, message: '' }), 3000);
        setLoading(false);
        return;
      }

      const response = await authenticatedFetch(`/api/internal-ads/placements/${placement._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to update placement.');
      }
    } catch (error) {
      console.error('Error updating placement:', error);
      alert('Error occurred while updating placement.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectToggle = (projectId) => {
    setFormData(prev => {
      const isSelected = prev.selectedProjects.includes(projectId);
      let nextSelected = [];
      let nextOverrides = [...(prev.projectOverrides || [])];
      if (isSelected) {
        nextSelected = prev.selectedProjects.filter(id => id !== projectId);
      } else {
        nextSelected = [...prev.selectedProjects, projectId];
        // Prefill EN from project details
        const exists = nextOverrides.find(po => String(po.projectId) === String(projectId));
        const sourceProject = availableProjects.find(p => String(p._id) === String(projectId)) || projects.find(p => String(p._id) === String(projectId));
        if (!exists) {
          nextOverrides.push({
            projectId,
            title: { en: sourceProject?.name || '' },
            description: { en: sourceProject?.description || '' }
          });
        } else {
          if (!exists.title?.en) exists.title = { ...exists.title, en: sourceProject?.name || '' };
          if (!exists.description?.en) exists.description = { ...exists.description, en: sourceProject?.description || '' };
        }
      }
      return { ...prev, selectedProjects: nextSelected, projectOverrides: nextOverrides };
    });
  };

  const upsertProjectOverride = (projectId, lang, field, value) => {
    setFormData(prev => {
      const overrides = [...(prev.projectOverrides || [])];
      const idx = overrides.findIndex(po => String(po.projectId) === String(projectId));
      if (idx === -1) {
        const newEntry = { projectId, title: {}, description: {} };
        newEntry[field][lang] = value;
        overrides.push(newEntry);
      } else {
        const entry = { ...overrides[idx] };
        entry[field] = { ...(entry[field] || {}), [lang]: value };
        overrides[idx] = entry;
      }
      return { ...prev, projectOverrides: overrides };
    });
  };

  const removeProjectOverrideLang = (projectId, lang) => {
    setFormData(prev => {
      const overrides = [...(prev.projectOverrides || [])];
      const idx = overrides.findIndex(po => String(po.projectId) === String(projectId));
      if (idx === -1) return prev;
      const entry = { ...overrides[idx] };
      if (entry.title && Object.prototype.hasOwnProperty.call(entry.title, lang)) {
        const { [lang]: _omit, ...rest } = entry.title;
        entry.title = rest;
      }
      if (entry.description && Object.prototype.hasOwnProperty.call(entry.description, lang)) {
        const { [lang]: _omit2, ...rest2 } = entry.description;
        entry.description = rest2;
      }
      overrides[idx] = entry;
      return { ...prev, projectOverrides: overrides };
    });
  };

  const supportedLangs = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' }
  ];

  const [customLangs, setCustomLangs] = useState([]);
  const [newLangCode, setNewLangCode] = useState('');
  const [newLangLabel, setNewLangLabel] = useState('');
  const [newLangFlag, setNewLangFlag] = useState('');
  const [showAddLangForm, setShowAddLangForm] = useState(false);
  const [isCustomMode, setIsCustomMode] = useState(false);

  useEffect(() => {
    if (placement.projectOverrides) {
      const defaultCodes = new Set(supportedLangs.map(l => l.code));
      const detected = [];
      placement.projectOverrides.forEach(po => {
        const titleLangs = Object.keys(po.title || {});
        const descLangs = Object.keys(po.description || {});
        const allKeys = Array.from(new Set([...titleLangs, ...descLangs]));
        allKeys.forEach(code => {
          if (!defaultCodes.has(code) && !detected.some(d => d.code === code)) {
            detected.push({
              code,
              label: code.toUpperCase(),
              flag: '🏳️'
            });
          }
        });
      });
      if (detected.length > 0) {
        setCustomLangs(prev => {
          const next = [...prev];
          detected.forEach(d => {
            if (!next.some(n => n.code === d.code)) {
              next.push(d);
            }
          });
          return next;
        });
      }
    }
  }, [placement]);

  const allLangs = [...supportedLangs, ...customLangs];

  // Removed general Ad Content override handlers


  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      {/* Error Toast (right side) */}
      {errorToast.show && (
        <div className="fixed top-4 right-4 z-[60] px-4 py-2 rounded bg-red-600 text-white shadow">
          {errorToast.message}
        </div>
      )}
      <div className="relative mx-auto my-8 p-1 sm:p-4 border w-[95%] max-w-7xl shadow-2xl rounded-2xl bg-gray-900 border-gray-700">
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex items-center justify-between mb-8 border-b border-gray-800 pb-6">
            <h3 className="text-2xl font-bold text-white tracking-tight">Edit Placement</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200"
            >
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name + Status same row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2 text-left">Placement Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter placement name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 text-left">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setFormData(prev => ({ ...prev, status: newStatus, isActive: newStatus === 'active' }));
                  }}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Platform Status */}
            <div className="mb-6 bg-gray-800/40 rounded-xl border border-gray-700 p-4">
              <label className="block text-sm font-semibold text-gray-300 mb-3 text-left">
                Platform Status (Active / Passive)
              </label>
              <div className="grid grid-cols-3 gap-4">
                <label className="flex items-center space-x-3 p-3 bg-gray-750 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.platformStatus?.android ?? true}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      platformStatus: { ...prev.platformStatus, android: e.target.checked }
                    }))}
                    className="rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-200">Android</span>
                </label>
                <label className="flex items-center space-x-3 p-3 bg-gray-750 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.platformStatus?.ios ?? true}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      platformStatus: { ...prev.platformStatus, ios: e.target.checked }
                    }))}
                    className="rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-200">iOS</span>
                </label>
                <label className="flex items-center space-x-3 p-3 bg-gray-750 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.platformStatus?.web ?? true}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      platformStatus: { ...prev.platformStatus, web: e.target.checked }
                    }))}
                    className="rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-200">Web</span>
                </label>
              </div>
            </div>

              <label className="block text-sm font-semibold text-gray-300 mb-2 text-left">
                Projects to Show in Ads
              </label>
              <p className="text-sm text-gray-400 mb-6 text-left">
                Select which projects should be advertised in this placement. The current project ({placement.projectId?.name}) will not be shown.
              </p>
              <div className="max-h-[65vh] overflow-y-auto bg-gray-800/50 rounded-xl border border-gray-700 p-6 space-y-4 custom-scrollbar">
                {availableProjects.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No other projects available to advertise.</p>
                ) : (
                  availableProjects.map((project) => (
                    <div key={project._id} className="p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id={`project-${project._id}`}
                          checked={formData.selectedProjects.includes(project._id)}
                          onChange={() => handleProjectToggle(project._id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {project.logo ? (
                            <img
                              src={project.logo.startsWith('http') ? project.logo : `${getBaseUrl()}${project.logo}`}
                              alt={project.name}
                              className="h-8 w-8 rounded-md object-cover border border-gray-600 flex-shrink-0"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-md bg-gray-700 flex items-center justify-center text-gray-400 text-xs border border-gray-600 flex-shrink-0">PRJ</div>
                          )}
                          <div className="flex flex-col min-w-0">
                            <label htmlFor={`project-${project._id}`} className="text-base text-white cursor-pointer font-semibold truncate text-left">
                              {project.name}
                            </label>
                            {project.website && (
                              <a 
                                href={project.website.startsWith('http') ? project.website : `https://${project.website}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center mt-1 truncate transition-colors text-left"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FaGlobe className="mr-1 h-2.5 w-2.5" />
                                {project.website.replace(/^https?:\/\//, '')}
                                <FaExternalLinkAlt className="ml-1 h-2 w-2" />
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Status/Expand Indicator */}
                        <div className="flex items-center space-x-2">
                          {!formData.selectedProjects.includes(project._id) ? (
                            <span className="text-[10px] text-gray-500 uppercase font-bold px-1.5 py-0.5 bg-gray-900/50 rounded border border-gray-700">Inactive</span>
                          ) : (
                            <span className="text-[10px] text-green-400 uppercase font-bold px-1.5 py-0.5 bg-green-900/20 rounded border border-green-900/30">Active</span>
                          )}
                          {formData.selectedProjects.includes(project._id) ? <FaChevronUp className="text-gray-500 w-3 h-3" /> : <FaChevronDown className="text-gray-500 w-3 h-3" />}
                        </div>
                      </div>

                      {/* Language overrides for this project - Only visible when checked */}
                      {formData.selectedProjects.includes(project._id) && (
                        <div className="mt-4 pl-7 pt-4 border-t border-gray-700/50 space-y-4 animate-fadeIn">
                          <div className="flex items-center space-x-2 text-gray-400 mb-2">
                            <FaLanguage className="h-3 w-3" />
                            <span className="text-xs font-semibold uppercase tracking-wider">Language Overrides</span>
                          </div>
                          
                          <div className="flex flex-wrap gap-2">
                            {allLangs.map(lang => {
                              const po = formData.projectOverrides?.find(po => String(po.projectId) === String(project._id));
                              const hasTitleKey = !!po && po.title && Object.prototype.hasOwnProperty.call(po.title, lang.code);
                              const hasDescKey = !!po && po.description && Object.prototype.hasOwnProperty.call(po.description, lang.code);
                              const isActive = lang.code === 'en' || hasTitleKey || hasDescKey;
                              return (
                                <div key={lang.code} className={`flex items-center rounded-lg text-xs border transition-all duration-200 ${isActive ? 'bg-indigo-600/20 border-indigo-500/50 text-white' : 'bg-gray-700/50 border-gray-600 text-gray-400 hover:bg-gray-650'}`} title={`${lang.flag} ${lang.label}`}>
                                  <button
                                    type="button"
                                    className="px-3 py-1.5 flex items-center"
                                    onClick={() => {
                                      const titleVal = lang.code === 'en' ? (po?.title?.en || project.name || '') : (po?.title?.[lang.code] || '');
                                      const descVal = lang.code === 'en' ? (po?.description?.en || project.description || '') : (po?.description?.[lang.code] || '');
                                      upsertProjectOverride(project._id, lang.code, 'title', titleVal);
                                      upsertProjectOverride(project._id, lang.code, 'description', descVal);
                                    }}
                                  >
                                    <span className="mr-2 text-sm">{lang.flag}</span>
                                    <span className="font-medium">{lang.code.toUpperCase()}</span>
                                  </button>
                                  {isActive && lang.code !== 'en' && (
                                    <button
                                      type="button"
                                      className="pr-2 pl-1 h-full flex items-center text-gray-400 hover:text-white transition-colors"
                                      onClick={(e) => { e.stopPropagation(); removeProjectOverrideLang(project._id, lang.code); }}
                                      aria-label={`Remove ${lang.label}`}
                                    >
                                      <FaTimes className="w-2.5 h-2.5" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}

                            {!showAddLangForm ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setShowAddLangForm(true);
                                  setIsCustomMode(false);
                                }}
                                className="px-3 py-1.5 flex items-center rounded-lg text-xs border bg-gray-800 border-dashed border-gray-600 text-gray-400 hover:bg-gray-750 hover:text-white transition-colors"
                              >
                                <span className="mr-1 text-sm font-bold">+</span>
                                <span>Add Lang</span>
                              </button>
                            ) : !isCustomMode ? (
                              <div className="flex flex-wrap items-center gap-1.5 bg-gray-800 border border-gray-700 p-1.5 rounded-lg animate-fadeIn">
                                <select
                                  onChange={e => {
                                    const val = e.target.value;
                                    if (val === 'custom') {
                                      setIsCustomMode(true);
                                    } else if (val) {
                                      const selected = PREDEFINED_LANGS.find(pl => pl.code === val);
                                      if (selected) {
                                        setCustomLangs(prev => [...prev, selected]);
                                        setShowAddLangForm(false);
                                        setIsCustomMode(false);
                                      }
                                    }
                                  }}
                                  defaultValue=""
                                  className="px-2.5 py-1 bg-gray-900 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none max-w-[180px]"
                                >
                                  <option value="" disabled>-- Select Language --</option>
                                  {PREDEFINED_LANGS.filter(pl => !allLangs.some(al => al.code === pl.code)).map(pl => (
                                    <option key={pl.code} value={pl.code}>
                                      {pl.flag} {pl.label} ({pl.code.toUpperCase()})
                                    </option>
                                  ))}
                                  <option value="custom">Custom...</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowAddLangForm(false);
                                    setIsCustomMode(false);
                                  }}
                                  className="p-1 text-gray-400 hover:text-white"
                                >
                                  <FaTimes className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center gap-1.5 bg-gray-800 border border-gray-700 p-1.5 rounded-lg animate-fadeIn">
                                <input
                                  type="text"
                                  placeholder="Code (e.g. it)"
                                  value={newLangCode}
                                  onChange={e => setNewLangCode(e.target.value.toLowerCase().replace(/[^a-z-]/g, '').slice(0, 5))}
                                  className="w-16 px-1.5 py-1 bg-gray-900 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none"
                                />
                                <input
                                  type="text"
                                  placeholder="Label (e.g. Italiano)"
                                  value={newLangLabel}
                                  onChange={e => setNewLangLabel(e.target.value)}
                                  className="w-24 px-1.5 py-1 bg-gray-900 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none"
                                />
                                <input
                                  type="text"
                                  placeholder="Flag (e.g. 🇮🇹)"
                                  value={newLangFlag}
                                  onChange={e => setNewLangFlag(e.target.value)}
                                  className="w-16 px-1.5 py-1 bg-gray-900 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const code = newLangCode.trim();
                                    if (!code) return;
                                    const label = newLangLabel.trim() || code.toUpperCase();
                                    const flag = newLangFlag.trim() || '🏳️';
                                    if (allLangs.some(l => l.code === code)) {
                                      alert('Language already exists!');
                                      return;
                                    }
                                    setCustomLangs(prev => [...prev, { code, label, flag }]);
                                    setNewLangCode('');
                                    setNewLangLabel('');
                                    setNewLangFlag('');
                                    setShowAddLangForm(false);
                                    setIsCustomMode(false);
                                  }}
                                  className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs transition-colors font-semibold"
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowAddLangForm(false);
                                    setIsCustomMode(false);
                                    setNewLangCode('');
                                    setNewLangLabel('');
                                    setNewLangFlag('');
                                  }}
                                  className="p-1 text-gray-400 hover:text-white"
                                >
                                  <FaTimes className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Render inputs for existing languages of this project */}
                          <div className="grid grid-cols-1 gap-3">
                            {(() => {
                              const po = formData.projectOverrides?.find(po => String(po.projectId) === String(project._id));
                              const dynamicCodes = po ? Array.from(new Set([...Object.keys(po.title || {}), ...Object.keys(po.description || {})])) : [];
                              const langCodes = ['en', ...dynamicCodes.filter(c => c !== 'en')];
                              return langCodes.map(code => (
                                <div key={`${project._id}-${code}`} className="bg-gray-900/30 p-3 rounded-lg border border-gray-700/30">
                                  <div className="flex items-center mb-2">
                                    <span className="text-sm mr-2">{allLangs.find(l => l.code === code)?.flag || '🏳️'}</span>
                                    <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">{allLangs.find(l => l.code === code)?.label || code}</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                      <label className="text-[10px] font-medium text-gray-500 ml-1 uppercase">Title</label>
                                      <input
                                        type="text"
                                        value={(po?.title?.[code] !== undefined) ? po.title[code] : (code === 'en' ? (project.name || '') : '')}
                                        onChange={(e) => upsertProjectOverride(project._id, code, 'title', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800/50 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all font-medium"
                                        placeholder="Enter display title..."
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <label className="text-[10px] font-medium text-gray-500 ml-1 uppercase">Description</label>
                                      <input
                                        type="text"
                                        value={(po?.description?.[code] !== undefined) ? po.description[code] : (code === 'en' ? (project.description || '') : '')}
                                        onChange={(e) => upsertProjectOverride(project._id, code, 'description', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800/50 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all font-medium"
                                        placeholder="Enter display description..."
                                      />
                                    </div>
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

            <div className="flex justify-end items-center pt-6 border-t border-gray-700">
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors font-medium"
                >
                  {loading ? 'Updating...' : 'Update Placement'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPlacementModal;
